import React from 'react';
import DefaultErrorFallback from '@/components/DefaultErrorFallback';
import { errorReportingService } from '../../../services/errorReporting.service';

interface Props {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary - Composant pour capturer et gérer les erreurs React
 *
 * Capture les erreurs dans l'arbre des composants enfants et affiche
 * une UI de fallback au lieu de crasher toute l'application.
 *
 * @example
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  /**
   * Appelé quand une erreur est lancée dans un composant enfant
   */
  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  /**
   * Appelé après qu'une erreur a été capturée
   * Utilisé pour logger l'erreur
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log l'erreur au service de reporting
    errorReportingService.logError({
      error: error.message,
      stack: error.stack || '',
      componentStack: errorInfo.componentStack || '',
      timestamp: new Date(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    });

    // Callback personnalisé si fourni
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  /**
   * Réinitialise l'état d'erreur pour permettre une nouvelle tentative
   */
  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
